import { SerializedConstructor } from '@langchain/core/load/serializable';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { mutate as mutateGlobal } from 'swr';

import {
  EXTENDED_STATS_RUN_SELECT,
  SESSION_EVALUATOR_STARTER_CODE,
} from '@/Pages/Settings/utils/constants';
import { useRecentRunsAndExamples } from '@/Pages/Settings/utils/useRecentRuns';
import { RulesCustomEvaluatorForm } from '@/components/Rules/RulesCustomEvaluatorForm';
import { FetcherParams } from '@/data/fetcher';
import { Button } from '@/design-system';
import { useCanAlignEvaluators } from '@/hooks/useCanAlignEvaluators';
import { useOrganizationId, useRunRulesMutation } from '@/hooks/useSwr';
import {
  RuleSchema,
  SessionRuleMutateBody,
  StructuredEvaluatorSchema,
} from '@/types/schema';
import {
  appEvaluatorPath,
  appOrganizationPath,
  appProjectsPath,
  appSessionPath,
} from '@/utils/constants';

import { ExpandableErrorAlert } from '../ExpandableErrorAlert';
import {
  DEFAULT_FILTER_STRING,
  useTabAwareFilters,
} from '../Rules/hooks/useTabAwareFilters';
import { useSearchModel } from '../RunsTable/hooks/useSearchModel';
import { SearchModel } from '../RunsTable/types';
import { HeaderTitleActionSlot, SplitViewPane } from '../SplitViewPane';
import useToast from '../Toast/useToast';
import { SessionEvaluatorCrudForm } from './EvaluatorCrudForm';
import { SessionsEvaluatorCrudPaneHeader } from './EvaluatorCrudPaneHeader';
import { SessionPreviewPanel } from './PreviewPanel';
import { SessionRuleForm } from './SessionRuleForm';
import {
  SESSION_EVAL_CRUD_PANE_WIDTH,
  SESSION_EVAL_EXPANDED_CRUD_PANE_WIDTH,
} from './constants';
import { useEvaluatorStore } from './store/evaluatorStore';
import { SessionEvaluatorCrudPaneProps } from './types';
import { reinsertFewShotToSerializedManifest } from './utils/fewShotExamplesUtils';
import { useCommitAndSaveRule } from './utils/useCommitAndSaveRule';

function SessionEvaluatorForm(
  props: {
    initialData?: RuleSchema;
    runTableFilters?: SearchModel;
    setRunTableFilters: (model: SearchModel) => void;
    alignQueueId?: string;
  } & Omit<SessionEvaluatorCrudPaneProps, 'onEvaluatorSaved'>
) {
  const importEvaluator = useEvaluatorStore((state) => state.importEvaluator);
  const promptMessages = useEvaluatorStore((state) => state.promptMessages);
  const hubRef = useEvaluatorStore((state) => state.hubRef);

  // Determine when the main CRUD form can be rendered
  const isNewEvaluator = !props.initialData;
  const isNewPrebuiltEvaluator = props.evaluator;
  const hasLoadedEvaluatorData = Boolean(
    props.initialData && (promptMessages || hubRef)
  );
  const hasLoadedPrebuiltEvaluatorData = Boolean(
    props.evaluator && promptMessages
  );

  // only render the CRUD form if:
  // 1. it's a new evaluator and it's not a prebuilt evaluator
  // 2. it's a new prebuilt evaluator and the data has loaded
  // 3. the evaluator data has loaded
  // otherwise manifest messages might not be updated correctly
  const shouldRenderCrudForm =
    (isNewEvaluator && !isNewPrebuiltEvaluator) ||
    (isNewPrebuiltEvaluator && hasLoadedPrebuiltEvaluatorData) ||
    hasLoadedEvaluatorData;

  const [showSampleData, setShowSampleData] = useState(
    props.defaultShowSampleData ?? true
  );
  const handleShowSampleData = (show: boolean) => {
    setShowSampleData(show);
    props.setPaneWidth?.(
      show
        ? SESSION_EVAL_EXPANDED_CRUD_PANE_WIDTH
        : SESSION_EVAL_CRUD_PANE_WIDTH
    );
  };

  const { open, setOpen } = props;
  const setFewShotEnabled = useEvaluatorStore(
    (state) => state.setFewShotEnabled
  );
  const setNumFewShotExamples = useEvaluatorStore(
    (state) => state.setNumFewShotExamples
  );
  const setName = useEvaluatorStore((state) => state.setName);
  const setSamplingRate = useEvaluatorStore((state) => state.setSamplingRate);

  const [ruleForm, setRuleForm] = useState<{
    data: Partial<SessionRuleMutateBody>;
    isValid: boolean;
  }>({
    data: props.initialData ?? {},
    isValid: true,
  });

  const onFormChange = useCallback(
    (form: NonNullable<typeof ruleForm>) => {
      setRuleForm(form);
      if (form.data.display_name) {
        setName(form.data.display_name, true);
      }
      if (form.data.sampling_rate) {
        setSamplingRate(form.data.sampling_rate);
      }
    },
    [setName, setSamplingRate]
  );

  useEffect(() => {
    if (open) {
      importEvaluator(
        props.evaluator,
        'session',
        props.backfill_from,
        props.evaluatorVersion
      );
      setName(props.initialData?.display_name ?? '', true);
      setSamplingRate(props.initialData?.sampling_rate ?? 1);
      if (props.fewShotEnabled !== undefined) {
        setFewShotEnabled(props.fewShotEnabled);
      }
      if (props.numFewShotExamples !== undefined) {
        setNumFewShotExamples(props.numFewShotExamples);
      }
    }
  }, [
    open,
    props.evaluator,
    props.backfill_from,
    props.evaluatorVersion,
    importEvaluator,
    props.fewShotEnabled,
    props.numFewShotExamples,
    setFewShotEnabled,
    setNumFewShotExamples,
    setName,
    props.initialData,
    setSamplingRate,
  ]);

  const [filtersToUse, setFilters] = useTabAwareFilters({
    runTableFilters: props.runTableFilters,
    setRunTableFilters: props.setRunTableFilters,
    existingRuleFilters: props.initialData
      ? {
          filter: props.initialData?.filter ?? DEFAULT_FILTER_STRING,
          traceFilter: props.initialData?.trace_filter,
          treeFilter: props.initialData?.tree_filter,
        }
      : undefined,
  });

  const onSuccess = useCallback(() => {
    if (props.onSave) {
      props.onSave();
    }
    setOpen(false);
  }, [props, setOpen]);

  const hubRefManifest = useEvaluatorStore((state) => state.hubRefManifest);

  const fewShotEnabled = useEvaluatorStore((state) => state.fewShotEnabled);
  const fewShotContent = useEvaluatorStore((state) => state.fewShotContent);

  const transformManifest = useCallback(
    (manifest: SerializedConstructor) => {
      if (
        fewShotEnabled &&
        fewShotContent &&
        manifest?.kwargs?.first?.kwargs?.messages
      ) {
        return reinsertFewShotToSerializedManifest(manifest, fewShotContent);
      }
      return manifest;
    },
    [fewShotEnabled, fewShotContent]
  );

  const onSuccessSave = useCallback(async () => {
    await mutateGlobal((key: FetcherParams) => key?.url?.includes('commits'));
    onSuccess();
    setOpen(false);
  }, [setOpen, onSuccess]);

  const { saveRule, isSavingEvaluator } = useCommitAndSaveRule({
    sessionId: props.sessionId,
    existingRule: props.initialData,
    transformManifest,
    onSuccess: onSuccessSave,
    hubRefManifest,
    filter: filtersToUse.filter,
    traceFilter: filtersToUse.traceFilter,
    treeFilter: filtersToUse.treeFilter,
  });

  return (
    <>
      <HeaderTitleActionSlot.Fill>
        <SessionsEvaluatorCrudPaneHeader
          onClose={() => {
            setOpen(false);
          }}
          onSave={async (validatedEvaluator) => {
            await saveRule({ validatedEvaluator });
          }}
          isSaving={isSavingEvaluator}
          selectedEvaluator={props.initialData?.evaluators?.[0].structured}
          saveDisabled={!ruleForm?.isValid}
        />
      </HeaderTitleActionSlot.Fill>
      <PanelGroup direction={'horizontal'} className={'h-full'}>
        <Panel
          defaultSize={70}
          minSize={30}
          className="overflow-y-auto p-6 pl-1"
        >
          <div className="flex flex-col gap-4">
            <SessionRuleForm
              initialData={props.initialData}
              sessionId={props.sessionId}
              onFormChange={onFormChange}
              filters={filtersToUse}
              setFilters={setFilters}
            />

            {/* Render once evaluator data is available or when creating a new evaluator */}
            {shouldRenderCrudForm && (
              <SessionEvaluatorCrudForm
                sessionId={props.sessionId}
                filters={filtersToUse}
                fewShotDatasetId={props.correctionsDatasetId}
                evaluatorVersion={props.evaluatorVersion}
                isCreating={!props.evaluator}
                showSampleData={showSampleData}
                setShowSampleData={handleShowSampleData}
                alignQueueId={props.alignQueueId}
              />
            )}
          </div>
        </Panel>
        {showSampleData && (
          <>
            <PanelResizeHandle className="group relative flex items-stretch text-secondary data-[resize-handle-active]:text-brand-green-400">
              <div className="w-[2px] bg-tertiary group-hover:bg-brand-green-400" />
              <div className="absolute inset-y-0 -left-[4px] z-10 w-[9px] opacity-0" />
              <div className="absolute inset-y-0 w-[2px] bg-current opacity-0 transition-all" />
            </PanelResizeHandle>
            <Panel
              defaultSize={30}
              minSize={30}
              className="overflow-y-auto p-6"
            >
              <SessionPreviewPanel
                sessionId={props.sessionId}
                filters={filtersToUse}
                onClose={() => setShowSampleData(false)}
              />
            </Panel>
          </>
        )}
      </PanelGroup>
    </>
  );
}

export function SessionsEvaluatorRuleCrudPane(props: {
  initialData?: RuleSchema;
  sessionId: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  evaluator?: StructuredEvaluatorSchema;
  onSave: () => void;
  isSaving?: boolean;

  runTableFilters?: SearchModel;
  setRunTableFilters: (model: SearchModel) => void;
  // Purely for the SessionEvaluatorCrudPane to expand and collapse size of pane
  defaultShowSampleData?: boolean;
  setPaneWidth?: (width: number) => void;
}) {
  const { open, setOpen } = props;
  const [width, setWidth] = useState(SESSION_EVAL_CRUD_PANE_WIDTH);
  useEffect(() => {
    if (props.initialData) {
      setWidth(SESSION_EVAL_EXPANDED_CRUD_PANE_WIDTH);
    } else {
      setWidth(SESSION_EVAL_CRUD_PANE_WIDTH);
    }
  }, [props.initialData]);

  const { newRuleEvaluatorVersion } = useCanAlignEvaluators();

  const correctionsDatasetId = props.initialData?.['corrections_dataset_id'];
  const fewShotEnabled = props.initialData?.['use_corrections_dataset'];
  const numFewShotExamples = props.initialData?.['num_few_shot_examples'];
  const evaluatorVersion =
    props.initialData?.['evaluator_version'] ?? newRuleEvaluatorVersion;

  return (
    <SplitViewPane
      title={props.initialData ? 'Edit evaluator' : 'Create evaluator'}
      hideArrows
      open={open}
      onClose={() => setOpen(false)}
      defaultWidth={SESSION_EVAL_CRUD_PANE_WIDTH}
      overrideWidth={width}
    >
      {open && (
        <SessionEvaluatorForm
          {...props}
          setPaneWidth={setWidth}
          correctionsDatasetId={correctionsDatasetId}
          fewShotEnabled={fewShotEnabled}
          numFewShotExamples={numFewShotExamples}
          evaluatorVersion={evaluatorVersion}
          defaultShowSampleData={!!props.initialData}
          alignQueueId={props.initialData?.alignment_annotation_queue_id}
        />
      )}
    </SplitViewPane>
  );
}

function SessionCodeCrudForm(props: {
  initialData?: RuleSchema;
  sessionId: string;
  setPaneWidth?: (width: number) => void;
  onSaveSuccess?: () => void;
  runTableFilters?: SearchModel;
  setRunTableFilters: (model: SearchModel) => void;
}) {
  const { sessionId } = useParams();
  const { searchModel } = useSearchModel();
  const [ruleForm, setRuleForm] = useState<{
    data: Partial<SessionRuleMutateBody>;
    isValid: boolean;
  }>({
    data: props.initialData ?? {},
    isValid: true,
  });
  const { recentRuns } = useRecentRunsAndExamples({
    sessionId: sessionId,
    variant: 'session',
    filters: searchModel,
    select: ruleForm.data.include_extended_stats
      ? EXTENDED_STATS_RUN_SELECT
      : undefined,
  });
  const [code, setCode] = useState(
    props.initialData?.['code_evaluators']?.[0]?.code ??
      SESSION_EVALUATOR_STARTER_CODE
  );
  const [language, setLanguage] = useState(
    props.initialData?.['code_evaluators']?.[0]?.language ?? 'python'
  );
  const setName = useEvaluatorStore((state) => state.setName);
  const setSamplingRate = useEvaluatorStore((state) => state.setSamplingRate);

  const onFormChange = useCallback(
    (form: NonNullable<typeof ruleForm>) => {
      setRuleForm(form);
      if (form.data.display_name) {
        setName(form.data.display_name, true);
      }
      if (form.data.sampling_rate) {
        setSamplingRate(form.data.sampling_rate);
      }
    },
    [setName, setSamplingRate]
  );
  const saveRule = useRunRulesMutation(props.initialData?.id ?? null);
  const name = useEvaluatorStore((state) => state.name);

  const [filtersToUse, setFilters] = useTabAwareFilters({
    runTableFilters: props.runTableFilters,
    setRunTableFilters: props.setRunTableFilters,
    existingRuleFilters: props.initialData
      ? {
          filter: props.initialData?.filter ?? DEFAULT_FILTER_STRING,
          traceFilter: props.initialData?.trace_filter,
          treeFilter: props.initialData?.tree_filter,
        }
      : undefined,
  });

  const samplingRate = useEvaluatorStore((state) => state.samplingRate);

  const navigate = useNavigate();
  const organizationId = useOrganizationId();
  const currentUrl = useLocation();
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );

  const handleSave = async () => {
    if (!ruleForm?.isValid) {
      return;
    }
    if (!ruleForm.data.display_name) {
      setErrorMessage('Please enter a display name for the evaluator.');
      return;
    }
    const ruleToSave: SessionRuleMutateBody = {
      ...ruleForm.data,
      display_name: name,
      is_enabled: ruleForm.data.is_enabled ?? true,
      sampling_rate: samplingRate,
      filter: filtersToUse.filter ?? DEFAULT_FILTER_STRING,
      trace_filter: filtersToUse.traceFilter,
      tree_filter: filtersToUse.treeFilter,
      session_id: props.sessionId,
      code_evaluators: [{ code, language }],
    };
    await saveRule.trigger({ json: ruleToSave });
    props.onSaveSuccess?.();
    if (
      !props.initialData &&
      currentUrl.pathname.includes(`/${appProjectsPath}/`) &&
      !currentUrl.pathname.includes(`/${appEvaluatorPath}/`)
    ) {
      navigate(
        `/${appOrganizationPath}/${organizationId}/${appProjectsPath}/${appSessionPath}/${props.sessionId}?tab=2`
      );
    }
  };
  const codeObj = useMemo(() => ({ code, language }), [code, language]);

  return (
    <div className="flex flex-col gap-4 p-4">
      <HeaderTitleActionSlot.Fill>
        <Button onClick={handleSave} size="md" loading={saveRule.isMutating}>
          Save
        </Button>
      </HeaderTitleActionSlot.Fill>
      {(saveRule.error ?? errorMessage) && (
        <ExpandableErrorAlert error={saveRule.error ?? errorMessage} />
      )}
      <SessionRuleForm
        initialData={props.initialData}
        sessionId={props.sessionId}
        onFormChange={onFormChange}
        filters={filtersToUse}
        setFilters={setFilters}
        isCodeEvaluator={true}
      />
      <div className="px-4">
        <RulesCustomEvaluatorForm
          value={codeObj}
          onChange={({ code, language }) => {
            setCode(code);
            setLanguage(language ?? 'python');
          }}
          variant="session"
          onSave={handleSave}
          saveMutating={saveRule.isMutating}
          sampleRun={recentRuns.data?.runs?.[0]}
          defaultShowTestCode={false}
          setPaneWidth={props.setPaneWidth}
          includeExtendedStats={ruleForm.data.include_extended_stats}
        />
      </div>
    </div>
  );
}

export function SessionsCustomCodeEvaluatorCrudPane(props: {
  initialData?: RuleSchema;
  sessionId: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  runTableFilters?: SearchModel;
  setRunTableFilters: (model: SearchModel) => void;
  onSaveSuccess?: () => void;
}) {
  const { createToast } = useToast();
  const [width, setWidth] = useState(SESSION_EVAL_CRUD_PANE_WIDTH);
  useEffect(() => {
    if (props.initialData) {
      setWidth(SESSION_EVAL_EXPANDED_CRUD_PANE_WIDTH);
    }
  }, [props.initialData]);
  const setName = useEvaluatorStore((state) => state.setName);
  useEffect(() => {
    if (props.initialData) {
      setName(props.initialData.display_name);
    }
  }, [props.initialData, setName]);

  const onSaveSuccess = () => {
    props.setOpen(false);
    createToast({
      title: 'Code evaluator saved',
    });
    props.onSaveSuccess?.();
  };

  return (
    <SplitViewPane
      open={props.open}
      title={
        props.initialData ? 'Edit Code Evaluator' : 'Create Code Evaluator'
      }
      onClose={() => {
        props.setOpen(false);
      }}
      hideArrows
      defaultWidth={SESSION_EVAL_CRUD_PANE_WIDTH}
      overrideWidth={width}
    >
      {props.open && (
        <SessionCodeCrudForm
          key={props.initialData?.id}
          initialData={props.initialData}
          sessionId={props.sessionId}
          setPaneWidth={setWidth}
          onSaveSuccess={onSaveSuccess}
          runTableFilters={props.runTableFilters}
          setRunTableFilters={props.setRunTableFilters}
        />
      )}
    </SplitViewPane>
  );
}
